import { useMemo, useEffect } from 'react';
import useDropdown from '../useDropdown';
import useOfficeOptions, { type OfficeOptions } from '../useOfficeOptions';
import useUserOptions, { type UserOptions } from '../useUserOptions';
import type { OnChangeFunctionOf } from '~/components/molecule/Dropdown';
import type { SessionHydrationOfficeFieldsFragment } from '~/graphql/types';
import type { ExpandedUser } from '../useUsers';

const text = {
  noUser: 'Alle gebruikers',
};

type UseOfficeUserSelectionOptions = {
  withAllUsersOption?: boolean;
  initialOfficeId?: string | null;
  initialUserId?: string | null;
  noUserText?: string;
};

type UseOfficeUserSelectionReturn = {
  officeOptions: OfficeOptions;
  selectedOfficeIdx: number | null;
  onOfficeChange: OnChangeFunctionOf<SessionHydrationOfficeFieldsFragment | null>;
  selectedOffice: SessionHydrationOfficeFieldsFragment | null;

  userOptions: UserOptions;
  selectedUserIdx: number | null;
  onUserChange: OnChangeFunctionOf<ExpandedUser | null>;
  selectedUser: ExpandedUser | null;
  setUserSelectedIdx: (index: number) => void;
};

const useOfficeUserSelection = ({
  withAllUsersOption = true,
  initialOfficeId,
  initialUserId,
  noUserText = text.noUser,
}: UseOfficeUserSelectionOptions = {}): UseOfficeUserSelectionReturn => {
  const officeOptions = useOfficeOptions();
  const [
    selectedOfficeIdx,
    onOfficeChange,
    selectedOffice,
    setSelectedOfficeIdx,
  ] = useDropdown(officeOptions, 0);

  const userOptionsFromOffice = useUserOptions({
    officeId: selectedOffice?.id,
    withAllUsersOption,
  });

  const userOptions: UserOptions = useMemo(
    () =>
      selectedOffice == null
        ? [
            {
              label: noUserText,
              payload: null,
              isEmptySelectionOption: true,
              key: 'no-selection',
            },
          ]
        : userOptionsFromOffice,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedOffice, userOptionsFromOffice],
  );

  const [selectedUserIdx, onUserChange, selectedUser, setUserSelectedIdx] =
    useDropdown(userOptions, 0);

  useEffect(() => {
    const initialOfficeIndex = initialOfficeId
      ? officeOptions.findIndex(office => office.key === initialOfficeId)
      : 0;
    const initialUserIndex = initialUserId
      ? userOptions.findIndex(user => user.key === initialUserId)
      : 0;

    if (initialOfficeIndex !== -1) {
      setSelectedOfficeIdx(initialOfficeIndex);
    }

    if (initialUserIndex !== -1) {
      setUserSelectedIdx(initialUserIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOfficeId, initialUserId]);

  useEffect(() => {
    if (
      selectedUser != null &&
      userOptions.find(option => option.payload?.id === selectedUser.id) == null
    ) {
      setUserSelectedIdx(0);
    }

    if (
      selectedUser == null &&
      selectedUserIdx !== 0 &&
      selectedUserIdx !== null
    ) {
      setUserSelectedIdx(0);
    }
  }, [
    selectedUser,
    userOptions,
    selectedUserIdx,
    setUserSelectedIdx,
    initialOfficeId,
    officeOptions,
    initialUserId,
    setSelectedOfficeIdx,
  ]);

  return {
    officeOptions,
    selectedOfficeIdx,
    onOfficeChange,
    selectedOffice,

    userOptions,
    selectedUserIdx,
    onUserChange,
    selectedUser,
    setUserSelectedIdx,
  };
};

export default useOfficeUserSelection;
